/**
 * Lazy / optional Sentry initialization for the web app.
 *
 * Mirrors apps/api/src/shared/sentry.ts. To opt in:
 *
 *   1. `pnpm --filter @leadsync/web add @sentry/react`
 *   2. Set `VITE_SENTRY_DSN` (and optionally `VITE_SENTRY_ENVIRONMENT`)
 *      in the web app's env
 *   3. Rebuild — the dynamic import below picks the package up at boot
 *
 * Until then `captureException` falls back to console.error so nothing
 * is silently dropped.
 */

interface SentryReactLike {
  init: (opts: Record<string, unknown>) => void;
  captureException: (err: unknown) => void;
  ErrorBoundary?: unknown;
}

let sentry: SentryReactLike | null = null;
let initialized = false;

export async function initSentry(): Promise<void> {
  if (initialized) return;
  initialized = true;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  try {
    // Defeat Vite's static-analysis of the dynamic-import specifier so the
    // build doesn't try to resolve `@sentry/react` (which only gets installed
    // when the operator opts in). The /* @vite-ignore */ hint alone is
    // insufficient at the Rollup analysis layer; constructing the string at
    // runtime via a variable is the canonical workaround.
    const moduleName = ['@sentry', 'react'].join('/');
    const mod = (await (import(/* @vite-ignore */ moduleName) as Promise<unknown>).catch(
      () => null,
    )) as { default?: SentryReactLike } | SentryReactLike | null;
    const candidate = (mod && 'default' in mod ? mod.default : mod) as SentryReactLike | null;
    if (!candidate) {
      // eslint-disable-next-line no-console
      console.warn(
        '[sentry] VITE_SENTRY_DSN set but @sentry/react not installed — run `pnpm --filter @leadsync/web add @sentry/react`',
      );
      return;
    }
    sentry = candidate;
    sentry.init({
      dsn,
      environment:
        (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE,
      tracesSampleRate: 0.1,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[sentry] init failed (continuing without)', err);
  }
}

export function captureException(err: unknown): void {
  if (sentry) {
    sentry.captureException(err);
  } else {
    // eslint-disable-next-line no-console
    console.error('[captured]', err);
  }
}
